import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type DryRunResult, type Segment, type SendResult, type Subscriber } from '../api';
import { renderEmail } from '../../functions/_lib/template';
import {
  blocksToHtml,
  defaultBlock,
  type Block,
} from '../../functions/_lib/blocks';
import {
  downloadTemplateJson,
  isBuiltInId,
  saveBuiltInOverride,
  saveDraft,
  type Template,
} from '../lib/templates';
import { PreviewModal } from '../components/PreviewModal';

type ResultState =
  | { kind: 'sent'; data: SendResult; testEmail?: string }
  | { kind: 'dry'; data: DryRunResult }
  | { kind: 'error'; message: string }
  | { kind: 'info'; message: string };

const STARTER_BLOCKS: Block[] = [
  { ...defaultBlock('heading'), text: 'New release is out 🎉', level: 2 } as Block,
  { ...defaultBlock('paragraph'), text: "We just dropped a new pack. Here's what's inside…" } as Block,
  { ...defaultBlock('button'), text: 'Check it out', href: 'https://atomic-rose.com' } as Block,
];

export function ComposePage() {
  const location = useLocation();
  const navState = location.state as { template?: Template; toEmail?: string; segmentId?: number } | null;
  const initialTemplate = navState?.template;
  const initialToEmail = navState?.toEmail?.trim().toLowerCase() || null;
  const initialSegmentId = navState?.segmentId ?? null;
  const [subject, setSubject] = useState(initialTemplate?.subject ?? '');
  const [preheader, setPreheader] = useState(initialTemplate?.preheader ?? '');
  const [blocks, setBlocks] = useState<Block[]>(initialTemplate?.blocks ?? STARTER_BLOCKS);
  const editingBuiltInId = initialTemplate && isBuiltInId(initialTemplate.id) ? initialTemplate.id : null;

  const [allSubs, setAllSubs] = useState<Subscriber[]>([]);
  const [includedSources, setIncludedSources] = useState<Set<string>>(new Set());
  const [excludedRecipientEmails, setExcludedRecipientEmails] = useState<Set<string>>(new Set());
  const [showRecipientList, setShowRecipientList] = useState(false);
  const [subsLoaded, setSubsLoaded] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>(initialToEmail ? [initialToEmail] : []);
  const [personSearch, setPersonSearch] = useState('');

  useEffect(() => {
    api.listSubscribers()
      .then((r) => {
        setAllSubs(r.subscribers);
        // Default to NONE selected — user must explicitly opt in to recipients.
        setIncludedSources(new Set());
        setSubsLoaded(true);
      })
      .catch(() => setSubsLoaded(true));
  }, []);

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const seenEmails = new Set<string>();
    for (const s of allSubs) {
      if (s.status !== 'subscribed') continue;
      const key = s.email.trim().toLowerCase();
      if (!key || seenEmails.has(key)) continue;
      seenEmails.add(key);
      const src = s.source || '';
      counts.set(src, (counts.get(src) || 0) + 1);
    }
    return counts;
  }, [allSubs]);

  const filteredRecipients = useMemo(() => {
    const seen = new Set<string>();
    const out: Subscriber[] = [];
    for (const s of allSubs) {
      if (s.status !== 'subscribed') continue;
      if (!includedSources.has(s.source || '')) continue;
      const key = s.email.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
  }, [allSubs, includedSources]);

  useEffect(() => {
    const allowed = new Set(filteredRecipients.map((s) => s.email.trim().toLowerCase()));
    setExcludedRecipientEmails((prev) => {
      const next = new Set<string>();
      for (const email of prev) {
        if (allowed.has(email)) next.add(email);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [filteredRecipients]);

  const excludedCount = useMemo(() => {
    let total = 0;
    for (const s of filteredRecipients) {
      if (excludedRecipientEmails.has(s.email.trim().toLowerCase())) total++;
    }
    return total;
  }, [filteredRecipients, excludedRecipientEmails]);
  const includedRecipientCount = Math.max(0, filteredRecipients.length - excludedCount);

  function toggleTag(tag: string) {
    setIncludedSources((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }
  function selectAllTags() {
    setIncludedSources(new Set(sourceCounts.keys()));
  }
  function selectNoTags() {
    setIncludedSources(new Set());
  }
  function toggleRecipientExcluded(email: string) {
    const key = email.trim().toLowerCase();
    setExcludedRecipientEmails((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const subscribedSubs = useMemo(() => {
    const seen = new Set<string>();
    const out: Subscriber[] = [];
    for (const s of allSubs) {
      if (s.status !== 'subscribed') continue;
      const key = s.email.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
  }, [allSubs]);

  const subsByEmail = useMemo(() => {
    const map = new Map<string, Subscriber>();
    for (const s of subscribedSubs) map.set(s.email.trim().toLowerCase(), s);
    return map;
  }, [subscribedSubs]);

  const personMatches = useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return [];
    const chosen = new Set(selectedEmails);
    return subscribedSubs
      .filter((s) => !chosen.has(s.email.trim().toLowerCase()))
      .filter((s) => s.email.toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [personSearch, subscribedSubs, selectedEmails]);

  function addSelectedEmail(email: string) {
    const key = email.trim().toLowerCase();
    setSelectedEmails((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setPersonSearch('');
  }
  function removeSelectedEmail(email: string) {
    setSelectedEmails((prev) => prev.filter((e) => e !== email));
  }

  function getSourcesFilter(): string[] | undefined {
    if (includedSources.size === sourceCounts.size) return undefined;
    return Array.from(includedSources);
  }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [tracking, setTracking] = useState(false);
  const [isMarketing, setIsMarketing] = useState(false);
  const [recipientMode, setRecipientMode] = useState<'sources' | 'segment' | 'specific'>(
    initialToEmail ? 'specific' : initialSegmentId != null ? 'segment' : 'sources',
  );
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(initialSegmentId);
  const [segmentCount, setSegmentCount] = useState<number | null>(null);

  useEffect(() => {
    api.listSegments().then((r) => setSegments(r.segments)).catch(() => setSegments([]));
  }, []);

  useEffect(() => {
    if (recipientMode !== 'segment' || selectedSegmentId == null) {
      setSegmentCount(null);
      return;
    }
    const seg = segments.find((s) => s.id === selectedSegmentId);
    if (!seg) { setSegmentCount(null); return; }
    api.previewSegment(seg.query)
      .then((r) => setSegmentCount(r.count))
      .catch(() => setSegmentCount(null));
  }, [recipientMode, selectedSegmentId, segments]);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result && bannerRef.current) {
      bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  function updateBlock(id: string, patch: Partial<Block>) {
    setBlocks((bs) => bs.map((b) => (b.id === id ? ({ ...b, ...patch } as Block) : b)));
  }
  function deleteBlock(id: string) {
    setBlocks((bs) => bs.filter((b) => b.id !== id));
  }
  function moveBlock(id: string, dir: 'up' | 'down') {
    setBlocks((bs) => {
      const i = bs.findIndex((b) => b.id === id);
      const j = dir === 'up' ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= bs.length) return bs;
      const copy = [...bs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }
  function addBlock(type: Block['type'], at?: number) {
    setBlocks((bs) => {
      const block = defaultBlock(type);
      if (at == null) return [...bs, block];
      return [...bs.slice(0, at), block, ...bs.slice(at)];
    });
  }
  function duplicateBlock(id: string) {
    setBlocks((bs) => {
      const i = bs.findIndex((b) => b.id === id);
      if (i < 0) return bs;
      const original = bs[i];
      const copy: Block = { ...original, id: `${original.id}-${Date.now()}` } as Block;
      return [...bs.slice(0, i + 1), copy, ...bs.slice(i + 1)];
    });
  }

  function validate(opts: { requireConfirm?: boolean; requireTest?: boolean; requireRecipients?: boolean }): string | null {
    if (!subject.trim()) return 'Subject is required.';
    if (blocks.length === 0) return 'Add at least one block to the email.';
    if (opts.requireTest && !testEmail.trim().includes('@')) return 'Enter a valid test email.';
    if (opts.requireRecipients) {
      if (recipientMode === 'segment') {
        if (selectedSegmentId == null) return 'Pick a segment to send to.';
        if (segmentCount === 0) return 'This segment matches 0 subscribers.';
      } else if (recipientMode === 'specific') {
        if (selectedEmails.length === 0) return 'Pick at least one recipient.';
      } else {
        if (includedRecipientCount === 0) return 'No recipients selected. Pick at least one source and keep at least one recipient checked.';
      }
    }
    if (opts.requireConfirm && confirmText !== 'SEND') return 'Type SEND in the confirmation field.';
    return null;
  }

  async function withBusy(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setResult(null);
    try { await fn(); }
    catch (e) { setResult({ kind: 'error', message: (e as Error).message }); }
    finally { setBusy(false); }
  }

  async function onPreview() {
    const err = validate({});
    if (err) { setResult({ kind: 'error', message: err }); return; }
    const html = renderEmail({
      subject: subject.trim(),
      bodyHtml: await blocksToHtml(blocks),
      unsubscribeUrl: 'https://newsletter.atomic-rose.com/api/unsubscribe?e=preview&s=preview',
      recipientName: 'Friend',
      preheader: preheader.trim() || undefined,
      isMarketing,
    });
    setPreviewHtml(html);
  }

  async function onTestSend() {
    const err = validate({ requireTest: true });
    if (err) { setResult({ kind: 'error', message: err }); return; }
    await withBusy(async () => {
      const r = await api.sendTest(subject.trim(), blocks, testEmail.trim(), {
        preheader: preheader.trim() || undefined,
        tracking,
        isMarketing,
      });
      setResult({ kind: 'sent', data: r, testEmail: testEmail.trim() });
    });
  }

  async function onBroadcast() {
    const err = validate({ requireConfirm: true, requireRecipients: true });
    if (err) { setResult({ kind: 'error', message: err }); return; }
    await withBusy(async () => {
      const opts: Parameters<typeof api.send>[2] = {
        preheader: preheader.trim() || undefined,
        tracking,
        isMarketing,
      };
      if (recipientMode === 'specific') {
        opts.emails = selectedEmails;
      } else if (recipientMode === 'segment' && selectedSegmentId != null) {
        opts.segmentId = selectedSegmentId;
      } else {
        opts.sources = getSourcesFilter();
        opts.excludedEmails = filteredRecipients
          .map((s) => s.email.trim().toLowerCase())
          .filter((email) => excludedRecipientEmails.has(email));
      }
      const r = await api.send(subject.trim(), blocks, opts);
      setResult({ kind: 'sent', data: r });
      setConfirmText('');
    });
  }

  async function onDryRun() {
    await withBusy(async () => {
      const r = await api.dryRun(subject.trim() || '(no subject)', blocks, getSourcesFilter());
      setResult({ kind: 'dry', data: r });
    });
  }

  async function onSaveAsDraft() {
    const name = prompt('Name this draft:', subject.trim() || 'Untitled draft');
    if (!name) return;
    try {
      await saveDraft({
        name: name.trim(),
        subject: subject.trim() || undefined,
        preheader: preheader.trim() || undefined,
        blocks,
      });
      setResult({ kind: 'info', message: `Draft "${name}" saved. Find it in the Templates tab.` });
    } catch (e) {
      setResult({ kind: 'error', message: `Save failed: ${(e as Error).message}` });
    }
  }

  function onExportJson() {
    const name = prompt('Filename (used as template name):', subject.trim() || initialTemplate?.name || 'template');
    if (!name) return;
    downloadTemplateJson({
      id: '',
      name: name.trim(),
      subject: subject.trim() || undefined,
      preheader: preheader.trim() || undefined,
      blocks,
    });
  }

  function onSaveBuiltInOverride() {
    if (!editingBuiltInId) return;
    saveBuiltInOverride(editingBuiltInId, {
      name: initialTemplate?.name || editingBuiltInId,
      description: initialTemplate?.description,
      subject: subject.trim() || undefined,
      preheader: preheader.trim() || undefined,
      blocks,
    });
    setResult({
      kind: 'info',
      message: `Built-in template "${initialTemplate?.name}" updated in your browser. To make this permanent for everyone, click Export JSON and replace src/templates/${editingBuiltInId}.json.`,
    });
  }

  return (
    <>
      {result && <ResultBanner ref={bannerRef} result={result} />}

      <div className="card">
        {editingBuiltInId && (
          <div className="muted" style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: 8, fontSize: 13 }}>
            Editing built-in template: <strong>{initialTemplate?.name}</strong>. Save changes locally to this browser, or Export JSON to update the file in code.
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
          {editingBuiltInId && (
            <button className="primary" onClick={onSaveBuiltInOverride} disabled={busy} style={{ fontSize: 13, padding: '6px 14px' }}>
              Save changes to built-in
            </button>
          )}
          <button className="secondary" onClick={onSaveAsDraft} disabled={busy} style={{ fontSize: 13, padding: '6px 14px' }}>
            Save as draft
          </button>
          <button className="secondary" onClick={onExportJson} disabled={busy} style={{ fontSize: 13, padding: '6px 14px' }}>
            Export JSON
          </button>
        </div>

        <div className="field">
          <label htmlFor="subject">Subject *</label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="New release: ..."
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="preheader">Preheader (preview text shown next to subject)</label>
          <input
            id="preheader"
            type="text"
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Optional — defaults to subject"
            disabled={busy}
          />
        </div>

        <div className="field">
          <label>Body</label>
          <div className="blocks">
            {blocks.length === 0 && (
              <p className="muted" style={{ textAlign: 'center', padding: 24 }}>
                No blocks yet. Click "+ Add block" below.
              </p>
            )}
            {blocks.map((block, i) => (
              <BlockEditor
                key={block.id}
                block={block}
                onUpdate={(patch) => updateBlock(block.id, patch)}
                onDelete={() => deleteBlock(block.id)}
                onDuplicate={() => duplicateBlock(block.id)}
                onMoveUp={() => moveBlock(block.id, 'up')}
                onMoveDown={() => moveBlock(block.id, 'down')}
                canMoveUp={i > 0}
                canMoveDown={i < blocks.length - 1}
                disabled={busy}
              />
            ))}
            <AddBlockMenu onAdd={(type) => addBlock(type)} disabled={busy} />
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Header, footer, and personalized unsubscribe link are added automatically. Images are auto-wrapped on a white card so transparent logos look clean on the dark theme.
          </p>
        </div>

        <div className="field">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ marginBottom: 0 }}>Recipients</label>
            <div className="row" style={{ gap: 6, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="recipient-mode"
                  checked={recipientMode === 'sources'}
                  onChange={() => setRecipientMode('sources')}
                  disabled={busy}
                />
                Sources
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="recipient-mode"
                  checked={recipientMode === 'segment'}
                  onChange={() => setRecipientMode('segment')}
                  disabled={busy}
                />
                Segment
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="recipient-mode"
                  checked={recipientMode === 'specific'}
                  onChange={() => setRecipientMode('specific')}
                  disabled={busy}
                />
                Specific people
              </label>
            </div>
          </div>

          {recipientMode === 'segment' ? (
            <div>
              {segments.length === 0 ? (
                <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
                  No segments saved yet. <Link to="/segments">Create one</Link> first.
                </p>
              ) : (
                <>
                  <select
                    value={selectedSegmentId ?? ''}
                    onChange={(e) => setSelectedSegmentId(e.target.value ? Number(e.target.value) : null)}
                    disabled={busy}
                    style={{
                      background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
                      borderRadius: 8, padding: '10px 12px', font: 'inherit', width: '100%',
                    }}
                  >
                    <option value="">— pick a segment —</option>
                    {segments.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {segmentCount != null && (
                    <div className="recipient-summary" style={{ marginTop: 8 }}>
                      Will send to <strong>{segmentCount}</strong> subscriber{segmentCount === 1 ? '' : 's'}.
                    </div>
                  )}
                </>
              )}
            </div>
          ) : recipientMode === 'specific' ? (
            <div>
              {selectedEmails.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {selectedEmails.map((email) => {
                    const sub = subsByEmail.get(email);
                    return (
                      <span
                        key={email}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          border: '1px solid var(--border)', borderRadius: 999,
                          padding: '4px 10px', fontSize: 13,
                        }}
                      >
                        {sub?.name ? <strong>{sub.name}</strong> : null}
                        <span className="muted">{email}</span>
                        <button
                          type="button"
                          className="link-btn"
                          title="Remove recipient"
                          onClick={() => removeSelectedEmail(email)}
                          disabled={busy}
                          style={{ padding: 0 }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <input
                type="text"
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                placeholder={subsLoaded ? 'Search by name or email to add…' : 'Loading subscribers…'}
                disabled={busy || !subsLoaded}
              />
              {personMatches.length > 0 && (
                <ul className="recipient-list" style={{ marginTop: 6 }}>
                  {personMatches.map((s) => (
                    <li key={s.email}>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => addSelectedEmail(s.email)}
                        disabled={busy}
                      >
                        + {s.name || '—'}
                        <span className="muted"> · {s.email}</span>
                        <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>{s.source || '—'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {personSearch.trim() && personMatches.length === 0 && subsLoaded && (
                <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                  No subscribed match for "{personSearch.trim()}".
                </p>
              )}
              <div className="recipient-summary" style={{ marginTop: 8 }}>
                Will send to <strong>{selectedEmails.length}</strong> recipient{selectedEmails.length === 1 ? '' : 's'}.
              </div>
            </div>
          ) : sourceCounts.size > 0 ? (
            <div className="row" style={{ gap: 4, marginBottom: 8 }}>
              <button type="button" className="link-btn" onClick={selectAllTags} disabled={busy}>
                All
              </button>
              <span className="muted">·</span>
              <button type="button" className="link-btn" onClick={selectNoTags} disabled={busy}>
                None
              </button>
            </div>
          ) : null}

          {recipientMode === 'sources' && (!subsLoaded ? (
            <p className="muted" style={{ marginTop: 8 }}>Loading subscribers…</p>
          ) : sourceCounts.size === 0 ? (
            <p className="muted" style={{ marginTop: 8 }}>No subscribed recipients found.</p>
          ) : (
            <>
              <div className="sources-grid">
                {Array.from(sourceCounts.entries()).map(([tag, count]) => (
                  <label key={tag || '_empty'} className="source-checkbox">
                    <input
                      type="checkbox"
                      checked={includedSources.has(tag)}
                      onChange={() => toggleTag(tag)}
                      disabled={busy}
                    />
                    <span>{tag || <em className="muted">(no tag)</em>}</span>
                    <span className="muted source-count">{count}</span>
                  </label>
                ))}
              </div>

              <div className="recipient-summary">
                Matched <strong>{filteredRecipients.length}</strong>, excluded <strong>{excludedCount}</strong>, final broadcast <strong>{includedRecipientCount}</strong>.
                {filteredRecipients.length > 0 && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setShowRecipientList((v) => !v)}
                    style={{ marginLeft: 8 }}
                  >
                    {showRecipientList ? 'Hide list' : 'Show list'}
                  </button>
                )}
              </div>

              {showRecipientList && filteredRecipients.length > 0 && (
                <ul className="recipient-list">
                  {filteredRecipients.map((s) => (
                    <li key={s.email}>
                      <label className="recipient-item">
                        <input
                          type="checkbox"
                          checked={!excludedRecipientEmails.has(s.email.trim().toLowerCase())}
                          onChange={() => toggleRecipientExcluded(s.email)}
                          disabled={busy}
                        />
                        <span>{s.name || <span className="muted">—</span>}</span>
                        <span className="muted"> · {s.email}</span>
                        <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>{s.source || '—'}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ))}
        </div>

        <div className="row" style={{ marginBottom: 24 }}>
          <button className="secondary" onClick={onPreview} disabled={busy}>Preview email</button>
          <button className="secondary" onClick={onDryRun} disabled={busy}>Dry run (count recipients)</button>
        </div>

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '8px 0 24px' }} />

        <div className="field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isMarketing}
              onChange={(e) => setIsMarketing(e.target.checked)}
              disabled={busy}
            />
            <span>Mark this email as marketing (use marketing footer text)</span>
          </label>
        </div>

        <div className="field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={tracking}
              onChange={(e) => setTracking(e.target.checked)}
              disabled={busy}
            />
            <span>Enable open + click tracking for this broadcast</span>
          </label>
          <p className="muted" style={{ margin: '4px 0 0 24px', fontSize: 12 }}>
            Adds a 1×1 pixel and rewrites links through <code>/api/track</code>. Off by default.
          </p>
        </div>

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '8px 0 24px' }} />

        <div className="field">
          <label htmlFor="testEmail">Send a test to one address first</label>
          <div className="row">
            <input
              id="testEmail"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={busy}
              style={{ maxWidth: 320 }}
            />
            <button className="primary" onClick={onTestSend} disabled={busy}>
              {busy ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </div>

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '24px 0' }} />

        <div className="field">
          <label htmlFor="confirm">
            Type <code>SEND</code> to broadcast to{' '}
            {recipientMode === 'specific'
              ? <>the <strong>{selectedEmails.length}</strong> selected recipient{selectedEmails.length === 1 ? '' : 's'}</>
              : 'ALL selected subscribers'}
          </label>
          <div className="row">
            <input
              id="confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SEND"
              disabled={busy}
              style={{ maxWidth: 200 }}
            />
            <button
              className="primary"
              onClick={onBroadcast}
              disabled={busy}
              style={{ background: 'var(--danger)' }}
            >
              {busy
                ? 'Sending…'
                : recipientMode === 'specific'
                  ? `Send to ${selectedEmails.length} recipient${selectedEmails.length === 1 ? '' : 's'}`
                  : 'Send to all subscribers'}
            </button>
          </div>
        </div>
      </div>

      {previewHtml && <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />}
    </>
  );
}

const BLOCK_LABELS: Record<Block['type'], string> = {
  heading: 'Heading',
  paragraph: 'Paragraph',
  image: 'Image',
  button: 'Button',
  youtube: 'YouTube',
};

type BlockEditorProps = {
  block: Block;
  onUpdate: (patch: Partial<Block>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled: boolean;
};

function BlockEditor(props: BlockEditorProps) {
  const { block, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, canMoveUp, canMoveDown, disabled } = props;
  return (
    <div className="block-editor">
      <div className="block-toolbar">
        <span className="block-type">{BLOCK_LABELS[block.type]}</span>
        <div className="block-actions">
          <IconButton title="Move up" onClick={onMoveUp} disabled={!canMoveUp || disabled}>↑</IconButton>
          <IconButton title="Move down" onClick={onMoveDown} disabled={!canMoveDown || disabled}>↓</IconButton>
          <IconButton title="Duplicate" onClick={onDuplicate} disabled={disabled}>⎘</IconButton>
          <IconButton title="Delete" onClick={onDelete} disabled={disabled}>×</IconButton>
        </div>
      </div>
      <div className="block-body">
        {block.type === 'heading' && <HeadingFields block={block} onUpdate={onUpdate} disabled={disabled} />}
        {block.type === 'paragraph' && <ParagraphFields block={block} onUpdate={onUpdate} disabled={disabled} />}
        {block.type === 'image' && <ImageFields block={block} onUpdate={onUpdate} disabled={disabled} />}
        {block.type === 'button' && <ButtonFields block={block} onUpdate={onUpdate} disabled={disabled} />}
        {block.type === 'youtube' && <YoutubeFields block={block} onUpdate={onUpdate} disabled={disabled} />}
      </div>
    </div>
  );
}

function IconButton({ children, title, onClick, disabled }: { children: React.ReactNode; title: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="icon-btn"
    >
      {children}
    </button>
  );
}

type FieldProps<T extends Block> = { block: T; onUpdate: (patch: Partial<T>) => void; disabled: boolean };

function HeadingFields({ block, onUpdate, disabled }: FieldProps<Extract<Block, { type: 'heading' }>>) {
  return (
    <>
      <input
        type="text"
        value={block.text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="Heading text"
        disabled={disabled}
      />
      <div className="row" style={{ marginTop: 8 }}>
        <Select
          label="Size"
          value={String(block.level)}
          onChange={(v) => onUpdate({ level: Number(v) as 1 | 2 | 3 })}
          options={[['1', 'H1 (largest)'], ['2', 'H2 (default)'], ['3', 'H3 (smallest)']]}
          disabled={disabled}
        />
        <Select
          label="Align"
          value={block.align}
          onChange={(v) => onUpdate({ align: v as 'left' | 'center' })}
          options={[['left', 'Left'], ['center', 'Center']]}
          disabled={disabled}
        />
      </div>
    </>
  );
}

function ParagraphFields({ block, onUpdate, disabled }: FieldProps<Extract<Block, { type: 'paragraph' }>>) {
  return (
    <>
      <textarea
        value={block.text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="Paragraph text. Press Enter for line breaks."
        disabled={disabled}
        style={{ minHeight: 100, fontFamily: 'inherit' }}
      />
      <div className="row" style={{ marginTop: 8 }}>
        <Select
          label="Align"
          value={block.align}
          onChange={(v) => onUpdate({ align: v as 'left' | 'center' })}
          options={[['left', 'Left'], ['center', 'Center']]}
          disabled={disabled}
        />
      </div>
    </>
  );
}

function ImageFields({ block, onUpdate, disabled }: FieldProps<Extract<Block, { type: 'image' }>>) {
  return (
    <>
      <input
        type="url"
        value={block.url}
        onChange={(e) => onUpdate({ url: e.target.value })}
        placeholder="https://atomic-rose.com/path-to-image.png"
        disabled={disabled}
      />
      <div className="row" style={{ marginTop: 8 }}>
        <input
          type="text"
          value={block.alt}
          onChange={(e) => onUpdate({ alt: e.target.value })}
          placeholder="Alt text (for accessibility)"
          disabled={disabled}
          style={{ flex: 2 }}
        />
        <input
          type="number"
          value={block.width}
          onChange={(e) => onUpdate({ width: Number(e.target.value) || 400 })}
          placeholder="Width"
          disabled={disabled}
          style={{ width: 100 }}
          min={50}
          max={560}
        />
        <Select
          label=""
          value={block.align}
          onChange={(v) => onUpdate({ align: v as 'left' | 'center' | 'right' })}
          options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]}
          disabled={disabled}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <input
          type="url"
          value={block.href || ''}
          onChange={(e) => onUpdate({ href: e.target.value || undefined })}
          placeholder="Optional: make image clickable (URL)"
          disabled={disabled}
        />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <Select
          label="Background"
          value={block.bg || 'none'}
          onChange={(v) => onUpdate({ bg: v as 'none' | 'white' })}
          options={[['none', 'None (flush, default)'], ['white', 'White card (for transparent logos)']]}
          disabled={disabled}
        />
      </div>
      {block.url && (
        <div
          style={{
            marginTop: 12,
            padding: block.bg === 'white' ? 12 : 0,
            background: block.bg === 'white' ? '#fff' : 'transparent',
            borderRadius: 8,
            textAlign: block.align as any,
          }}
        >
          <img src={block.url} alt={block.alt} style={{ maxWidth: Math.min(block.width, 400), maxHeight: 200 }} />
        </div>
      )}
    </>
  );
}

function ButtonFields({ block, onUpdate, disabled }: FieldProps<Extract<Block, { type: 'button' }>>) {
  return (
    <>
      <input
        type="text"
        value={block.text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="Button text"
        disabled={disabled}
      />
      <div style={{ marginTop: 8 }}>
        <input
          type="url"
          value={block.href}
          onChange={(e) => onUpdate({ href: e.target.value })}
          placeholder="https://atomic-rose.com/landing-page"
          disabled={disabled}
        />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <Select
          label="Align"
          value={block.align}
          onChange={(v) => onUpdate({ align: v as 'left' | 'center' })}
          options={[['left', 'Left'], ['center', 'Center']]}
          disabled={disabled}
        />
      </div>
    </>
  );
}

function YoutubeFields({ block, onUpdate, disabled }: FieldProps<Extract<Block, { type: 'youtube' }>>) {
  return (
    <>
      <input
        type="url"
        value={block.videoUrl}
        onChange={(e) => {
          const videoUrl = e.target.value;
          const derived = deriveYoutubeThumbnail(videoUrl);
          onUpdate({ videoUrl, thumbnailUrl: derived || block.thumbnailUrl });
        }}
        placeholder="https://www.youtube.com/watch?v=..."
        disabled={disabled}
      />
      <div style={{ marginTop: 8 }}>
        <input
          type="text"
          value={block.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Video title shown under thumbnail"
          disabled={disabled}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <input
          type="url"
          value={block.thumbnailUrl || ''}
          onChange={(e) => onUpdate({ thumbnailUrl: e.target.value || undefined })}
          placeholder="Optional custom thumbnail URL"
          disabled={disabled}
        />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <Select
          label="Align"
          value={block.align}
          onChange={(v) => onUpdate({ align: v as 'left' | 'center' })}
          options={[['left', 'Left'], ['center', 'Center']]}
          disabled={disabled}
        />
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        Email clients do not support embedded video players. This block sends a linked thumbnail (best practice).
      </p>
    </>
  );
}

function Select({ label, value, onChange, options, disabled }: {
  label: string; value: string; onChange: (v: string) => void; options: [string, string][]; disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {label && <span style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
          borderRadius: 8, padding: '8px 10px', font: 'inherit',
        }}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function AddBlockMenu({ onAdd, disabled }: { onAdd: (type: Block['type']) => void; disabled: boolean }) {
  const types: Block['type'][] = ['heading', 'paragraph', 'image', 'button', 'youtube'];
  return (
    <div className="add-block-menu">
      <span className="muted" style={{ fontSize: 12, marginRight: 8 }}>+ Add block:</span>
      {types.map((t) => (
        <button
          key={t}
          type="button"
          className="add-block-btn"
          onClick={() => onAdd(t)}
          disabled={disabled}
        >
          {BLOCK_LABELS[t]}
        </button>
      ))}
      <button
        type="button"
        className="add-block-btn"
        onClick={() => {
          onAdd('heading');
          onAdd('paragraph');
          onAdd('button');
        }}
        disabled={disabled}
      >
        Release section
      </button>
    </div>
  );
}

const ResultBanner = forwardRef<HTMLDivElement, { result: ResultState }>(({ result }, ref) => {
  if (result.kind === 'error') {
    return (
      <div ref={ref} className="error" style={{ scrollMarginTop: 16 }}>
        <strong>Error:</strong> {result.message}
      </div>
    );
  }
  if (result.kind === 'info') {
    return (
      <div ref={ref} className="success" style={{ scrollMarginTop: 16 }}>
        {result.message}
      </div>
    );
  }
  if (result.kind === 'dry') {
    return (
      <div ref={ref} className="success" style={{ scrollMarginTop: 16 }}>
        <strong>Dry run:</strong> would send to {result.data.recipients} recipients.
        {result.data.sample.length > 0 && (
          <> First few: {result.data.sample.map((s) => s.email).join(', ')}</>
        )}
      </div>
    );
  }
  const { sent, failed, errors } = result.data;
  const className = failed > 0 || sent === 0 ? 'error' : 'success';
  return (
    <div ref={ref} className={className} style={{ scrollMarginTop: 16 }}>
      <strong>{result.testEmail ? `Test send to ${result.testEmail}:` : 'Broadcast complete:'}</strong>
      {' '}sent <strong>{sent}</strong>, failed <strong>{failed}</strong>.
      {errors.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {errors.map((e, i) => (
            <li key={i}><code>{e.email}</code>: {e.error}</li>
          ))}
        </ul>
      )}
      {failed === 0 && sent > 0 && (
        <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
          Check your inbox (and spam folder — first sends from a new From address sometimes get filtered).
        </p>
      )}
    </div>
  );
});
ResultBanner.displayName = 'ResultBanner';

function deriveYoutubeThumbnail(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace('/', '').trim();
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v') || '';
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
  } catch {
    // noop
  }
  return '';
}

